// Satellite passes (GET devices/<mac>/overpasses/, POST .../schedule/): the
// server's answer read into what the card shows. Every value is checked, so
// anything that is not the endpoint's answer reads as null.

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v) => (typeof v === "string" ? v : null);

export function readPass(p) {
  if (!p || typeof p !== "object" || !str(p.satellite) || !str(p.utc)) return null;
  return {
    satellite: p.satellite,
    family: str(p.family),
    utc: p.utc,
    t: num(p.t),
    local: str(p.local),
    offNadirKm: num(p.off_nadir_km),
    viewAngle: num(p.view_angle_deg),
    halfSwathKm: num(p.half_swath_km),
    inSchedule: !!p.in_schedule,
    why: str(p.why),
  };
}

export function readOverpasses(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.passes)) return null;
  const o = data.orbits && typeof data.orbits === "object" ? data.orbits : null;
  const problem = data.problem && typeof data.problem === "object" ? data.problem : null;
  return {
    days: num(data.days),
    tzHours: num(data.tz_hours),
    passes: data.passes.map(readPass).filter(Boolean),
    orbits: o
      ? { ageH: num(o.age_h), stale: !!o.stale, error: str(o.error), fetchedAt: str(o.fetched_at) }
      : null,
    problem: problem ? { code: str(problem.code), detail: str(problem.detail) || "Passes unavailable" } : null,
  };
}

export function readPlan(data) {
  if (!data || typeof data !== "object" || typeof data.sched !== "string") return null;
  return {
    sched: data.sched,
    current: str(data.current) || "",
    describe: Array.isArray(data.describe) ? data.describe.filter((w) => typeof w === "string") : [],
    added: (Array.isArray(data.added) ? data.added : []).map(readPass).filter(Boolean),
    skipped: (Array.isArray(data.skipped) ? data.skipped : []).map(readPass).filter(Boolean),
    unchanged: !!data.unchanged,
    sent: !!data.sent,
    dryRun: !!data.dry_run,
    days: num(data.days),
    status: str(data.settings?.status),
  };
}

// "UTC+7"
export const zoneText = (h) =>
  h === null || h === undefined ? "" : `UTC${h >= 0 ? "+" : "−"}${Math.abs(h)}`;

// "Mon 26 Oct, 10:35:22" from the server's local "2026-10-26 10:35:22"
export function localText(local) {
  if (!local) return "–";
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(local);
  if (!m) return local;
  const day = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const name = day.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
  return `${name.replace(",", "")}, ${m[4]}`;
}

// "in 2 d 3 h", "in 45 min", "now"
export function untilText(t, now = Date.now()) {
  if (t === null || t === undefined) return "";
  const s = t - now / 1000;
  if (s < 60) return "now";
  const h = Math.floor(s / 3600);
  if (h < 1) return `in ${Math.round(s / 60)} min`;
  if (h < 48) return `in ${h} h`;
  return `in ${Math.floor(h / 24)} d ${h % 24} h`;
}

export const orbitsText = (o) => {
  if (!o) return "";
  if (o.ageH === null) return "Orbits: CelesTrak, age unknown.";
  const age = o.ageH < 48 ? `${Math.round(o.ageH)} h` : `${Math.round(o.ageH / 24)} days`;
  return `Orbits from CelesTrak, ${age} old${o.stale ? ` (a refresh failed${o.error ? `: ${o.error}` : ""}; still within seconds for a week or two)` : ""}.`;
};
